import React, { useState, useEffect } from 'react';
import { FaTimes, FaUpload } from 'react-icons/fa';
import Select from "react-select";  // install with: npm install react-select


const API_URL = '/api';

const initialFields = {
  company_name: '',
  email_id: '',
  mobile_no: '',
  password: '',
  company_logo: null,
  vendor_code: '',
  vendor_name: '',   // <-- added
  client_code: '',
};

const ClientFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading, vendors }) => {
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFields({ ...initialFields, ...initialData });
        setLogoPreview(null);
      } else {
        setFields(initialFields);
        setLogoPreview(null);
      }
      setErrors({});
    }
  }, [open, initialData, isEdit]);

  const handleChange = e => {
    const { name, value } = e.target;

    if (name === "vendor_code") {
      const selectedVendor = vendors.find(v => v.vendor_code === value);
      setFields({
        ...fields,
        vendor_code: value,
        vendor_name: selectedVendor ? selectedVendor.vendor_name : "",
      });
    } else {
      setFields({ ...fields, [name]: value });
    }
  };

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFields({ ...initialFields, ...initialData });
        setLogoPreview(null);
      } else {
        const fetchNextClientCode = async () => {
          try {
            const res = await fetch(`${API_URL}/clients/latest-code`);
            const data = await res.json();
            const latestCode = data.latestClientCode || 'CLT-000';
            
            const numberPart = parseInt(latestCode.replace('CLT-', '')) || 0;
            const nextCode = 'CLT-' + String(numberPart + 1).padStart(3, '0');
            
            setFields({ ...initialFields, client_code: nextCode });
          } catch (err) {
            console.error('Error fetching latest client code:', err);
            setFields({ ...initialFields, client_code: 'CLT-001' }); 
          }
        };

        fetchNextClientCode();
        setLogoPreview(null);
      }
      setErrors({});
    }
  }, [open, initialData, isEdit]);

  const handleFileChange = e => {
    const file = e.target.files[0];
    setFields({ ...fields, company_logo: file });
    setLogoPreview(file ? URL.createObjectURL(file) : null);
  };

  const validate = () => {
    const errs = {};
    if (!fields.company_name) errs.company_name = 'Required';
    if (!fields.email_id) errs.email_id = 'Required';
    if (!fields.mobile_no) errs.mobile_no = 'Required';
    if (!isEdit && !fields.password) errs.password = 'Required';
    if (!fields.vendor_code) errs.vendor_code = 'Vendor is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    const formData = new FormData();
    Object.keys(fields).forEach(key => {
      if (fields[key]) formData.append(key, fields[key]);
    });
    onSubmit(formData, isEdit);
  };

  if (!open) return null;

 return (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-5xl p-4 sm:p-8 relative overflow-y-auto max-h-[95vh]">
      <button className="absolute top-4 right-4 text-[#2b6777] hover:text-[#52ab98]" onClick={onClose}>
        <FaTimes size={20} />
      </button>
      
      <h2 className="text-xl sm:text-2xl font-bold text-[#2b6777] mb-4 sm:mb-6 text-center">
        {isEdit ? 'Edit Client' : 'Create New Client'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Client Information Section */}
        <div className='bg-white p-3 sm:p-4 rounded-xl shadow-md'>
          <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-2">Client Information</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            {/* Company Code */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left">
                Company code
              </label>
              <div className="w-full sm:w-3/4">
                <input
                  type="text"
                  name="client_code"
                  value={fields.client_code}
                  readOnly
                  className="w-full border rounded px-3 py-2 bg-gray-100 border-[#c8d8e4] text-gray-700 cursor-not-allowed"
                />
                {errors.company_code && <span className="text-xs text-red-500 mt-1 block">{errors.company_code}</span>}
              </div>
            </div>
            
            {/* Company Name */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left">
                Company Name
              </label>
              <div className="w-full sm:w-3/4">
                <input
                  type="text"
                  name="vendor_name"
                  value={fields.vendor_name}
                  readOnly
                  className="w-full border rounded px-3 py-2 bg-gray-100 border-[#c8d8e4] text-gray-700 cursor-not-allowed"
                />
              </div>
            </div>
            
            {/* Vendor Code */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left">
                Vendor Code*
              </label>
              <div className="w-full sm:w-3/4">
                <Select
                  options={vendors.map(v => ({
                    value: v.vendor_code,
                    label: `${v.vendor_code}`,
                    vendor_name: v.vendor_name,
                  }))}
                  value={
                    fields.vendor_code
                      ? {
                          value: fields.vendor_code,
                          label: `${fields.vendor_code}`,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    setFields({
                      ...fields,
                      vendor_code: selected ? selected.value : "",
                      vendor_name: selected ? selected.vendor_name : "",
                    })
                  }
                  placeholder="Search or select vendor..."
                  isClearable
                  className="w-full"
                  styles={{
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? "#52ab98" : "white",
                      color: state.isFocused ? "white" : "black",
                      cursor: "pointer",
                    }),
                    control: (provided) => ({
                      ...provided,
                      borderColor: "#c8d8e4",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#52ab98" },
                    }),
                  }}
                />
                {errors.vendor_code && (
                  <span className="text-xs text-red-500 mt-1 block">{errors.vendor_code}</span>
                )}
              </div>
            </div>
            
            {/* Vendor Name */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left">
                Vendor Name
              </label>
              <div className="w-full sm:w-3/4">
                <input
                  type="text"
                  name="vendor_name"
                  value={fields.vendor_name}
                  readOnly
                  className="w-full border rounded px-3 py-2 bg-gray-100 border-[#c8d8e4] text-gray-700 cursor-not-allowed"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Contact & Credentials Section */}
        <div className='bg-white p-3 sm:p-4 rounded-xl shadow-md'>
          <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mt-2 mb-2">Contact & Credentials</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            {/* Email */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left">
                Email *
              </label>
              <div className="w-full sm:w-3/4">
                <input 
                  name="email_id" 
                  value={fields.email_id} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.email_id ? 'border-red-400' : 'border-[#c8d8e4]'}`} 
                />
                {errors.email_id && <span className="text-xs text-red-500 mt-1 block">{errors.email_id}</span>}
              </div>
            </div>
            
            {/* Mobile */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left">
                Mobile No *
              </label>
              <div className="w-full sm:w-3/4">
                <input 
                  name="mobile_no" 
                  value={fields.mobile_no} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.mobile_no ? 'border-red-400' : 'border-[#c8d8e4]'}`} 
                />
                {errors.mobile_no && <span className="text-xs text-red-500 mt-1 block">{errors.mobile_no}</span>}
              </div>
            </div>
            
            {/* Password (only for create) */}
            {!isEdit && (
              <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
                <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left">
                  Password *
                </label>
                <div className="w-full sm:w-3/4">
                  <input 
                    type="password" 
                    name="password" 
                    value={fields.password} 
                    onChange={handleChange} 
                    className={`w-full border rounded px-3 py-2 ${errors.password ? 'border-red-400' : 'border-[#c8d8e4]'}`} 
                  />
                  {errors.password && <span className="text-xs text-red-500 mt-1 block">{errors.password}</span>}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Company Logo Section */}
        <div className='bg-white p-3 sm:p-4 rounded-xl shadow-md'>
          <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-3">Company Logo</h3>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="logo-upload" />
            <label 
              htmlFor="logo-upload" 
              className="flex items-center justify-center gap-2 px-4 py-2 bg-[#52ab98] text-white rounded cursor-pointer hover:bg-[#2b6777] w-full sm:w-auto"
            >
              <FaUpload /> Upload Logo
            </label>
            
            {logoPreview ? (
              <img src={logoPreview} alt="Logo Preview" className="h-16 rounded shadow" />
            ) : (isEdit && fields.client_id ? (
              <img 
                src={`/api/clients/${fields.client_id}/logo?${Date.now()}`} 
                alt="Logo" 
                className="h-16 rounded shadow" 
                onError={e => { e.target.onerror = null; e.target.src = ''; }} 
              />
            ) : (
              <span className="text-gray-400">No logo selected</span>
            ))}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-4 sm:mt-6">
          <button 
            type="button" 
            className="px-4 py-2 rounded bg-gray-200 text-[#2b6777] font-semibold w-full sm:w-auto order-2 sm:order-1"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="px-6 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow w-full sm:w-auto order-1 sm:order-2"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
          </button>
        </div>
      </form>
    </div>
  </div>
);
};

export default ClientFormModal; 